import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { useImages } from "../lib/images";
import api from "../lib/api";
import Seo from "../components/Seo";

// Static fallback — shown if no curriculum PDF has been uploaded yet
const STATIC_MODULES = (IMG) => [
  { n: 1, title: "Drawing", img: IMG.drawing, body: "The bedrock of every artist. Grades of graphite, textures, tonal values, sighting, enlarging with grids, perspective, portraiture and figurative drawing. Soon you'll be viewing the world as an artist does." },
  { n: 2, title: "Oil Pastels", img: IMG.oilPastel, body: "A beautiful bridge from pencil to wet mediums. Blending, colour theory, colour harmony, and how pigments sit on different coloured papers. Always a student surprise." },
  { n: 3, title: "Watercolours", img: IMG.watercolor, body: "Transparent, luminous and portable. Prepare your paper, learn washes, wet-on-wet, layering — many students' lifelong favourite." },
  { n: 4, title: "Acrylics", img: IMG.acrylic, body: "We end on a high — impasto, palette knives, abstracts and landscapes. You leave with a wide repertoire and a style beginning to emerge." },
];

const MEDIUM_COLOR = {
  "Drawing": "#A685BD",
  "Oil Pastels": "#E8C37A",
  "Watercolours": "#8EC9C7",
  "Acrylics": "#5D2C8F",
  "Mixed": "#6E6074",
};

export default function Courses() {
  const { images: IMG } = useImages();
  const [curr, setCurr] = useState(null);
  const [images, setImages] = useState([]);
  useEffect(() => {
    api.get("/public/curriculum").then((r) => setCurr(r.data.available ? r.data : null)).catch(() => {});
    api.get("/public/curriculum/images").then((r) => setImages(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="courses-page">
      <Seo path="/courses" title="Courses — 14-week drawing, watercolour, oils & acrylics"
        description="Our 14-week adult art course in East Croydon covers drawing, oil pastels, watercolours and acrylics. Small classes, warm atmosphere, all levels welcome." />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-12">
        <div className="font-hand text-[#A685BD] text-2xl">{curr ? curr.term : "14 weeks · 4 modules · one exhibition"}</div>
        <h1 className="font-display text-5xl md:text-6xl text-[#5D2C8F] mt-2 brush-underline">
          {curr ? curr.title : "Our 14-week art course"}
        </h1>
        <p className="mt-7 text-lg text-[#5A4A6B] max-w-3xl leading-relaxed">
          {curr?.overview || "Our course structure is progressive — tuition develops week-on-week. As you work through the syllabus, we build on teachings from previous classes so that your artistic skills grow as you go. Returning students also go on to explore new projects and other mediums."}
        </p>
      </section>

      {/* Weekly projects (when curriculum is loaded) */}
      {curr ? (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10">
          <div className="text-center mb-10">
            <div className="font-hand text-[#8EC9C7] text-2xl">week by week</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#5D2C8F]">Your 14 exciting projects</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {curr.weeks.map((w, idx) => {
              const img = images[idx] || images[idx % Math.max(1, images.length)];
              const colour = MEDIUM_COLOR[w.medium] || "#5D2C8F";
              return (
                <div key={w.number} className="soft-card overflow-hidden hover:-translate-y-1 transition-transform" data-testid={`week-card-${w.number}`}>
                  {img && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={img.src} alt={w.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-widest" style={{ color: colour }}>Week {w.number}</span>
                      {w.medium && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full border" style={{ color: colour, borderColor: colour + '66' }}>{w.medium}</span>
                      )}
                    </div>
                    <div className="font-display text-xl text-[#5D2C8F] mt-2 leading-tight">{w.title}</div>
                    <p className="text-sm text-[#5A4A6B] mt-2 leading-relaxed">{w.project}</p>
                    {w.learning_points?.length > 0 && (
                      <ul className="mt-3 space-y-1">
                        {w.learning_points.slice(0, 3).map((p) => (
                          <li key={`${w.week || w.title}-${p}`} className="text-xs text-[#6E6074] flex items-start gap-1.5">
                            <CheckCircle2 size={12} className="mt-0.5 shrink-0" style={{ color: colour }} /> {p}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ) : (
        // static fallback
        STATIC_MODULES(IMG).map((m, i) => (
          <section key={m.title} className={`${i % 2 ? "bg-[#F4EDDD]" : ""} py-16`}>
            <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-12 items-center">
              <div className={i % 2 ? "md:order-2" : ""}>
                <img src={m.img} alt={m.title} className="rounded-3xl aspect-[4/3] object-cover w-full shadow-lg" />
              </div>
              <div>
                <div className="font-hand text-[#8EC9C7] text-2xl">Module {m.n}</div>
                <h2 className="font-display text-4xl md:text-5xl text-[#5D2C8F] mt-1">{m.title}</h2>
                <p className="text-[#5A4A6B] mt-5 leading-relaxed">{m.body}</p>
              </div>
            </div>
          </section>
        ))
      )}

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="soft-card p-10 md:p-14 text-center">
          <div className="font-hand text-[#A685BD] text-2xl flex items-center justify-center gap-2">
            <Sparkles size={18} /> the grand finale
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-[#5D2C8F] mt-1">Week 14 — Exhibition</h2>
          <p className="text-[#5A4A6B] mt-5 max-w-2xl mx-auto leading-relaxed">
            At the end of the 14 weeks we hold an exhibition to celebrate your progress with the friends you've made
            and everyone you choose to invite. We supply the tickets, you supply the art.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/schedule" className="sac-btn-primary" data-testid="courses-schedule-btn">See sessions & pricing <ArrowRight size={18} /></Link>
            <Link to="/contact" className="sac-btn-secondary" data-testid="courses-enquire-btn">Enquire</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
